import {t, Trans} from '@lingui/macro';
import React, {useCallback} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {DaysOfTheWeekParam} from '../../../editors/commons/paramModel';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {
  currentTimezone,
  fromIsoDayOfWeek,
  isoDayOfWeekToZeroNumbered,
  sundayStartingDaysOfTheWeek,
  toIsoDayOfWeek,
  zeroNumberedToIsoDayOfWeek,
} from '../../ruleTimeHelpers';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {defaultDropdownWidth, GenericMultipleParameterProps} from './genericParameter';

/*
 * Types.
 */

interface DaysParameterProps
  extends Omit<GenericMultipleParameterProps<{id: number}>, 'selectedItems' | 'onChange'> {
  days: ReadonlyArray<number>;
  onDaysChange: HandlerOf<DaysOfTheWeekParam>;
}

/*
 * Constants.
 */

const items = sundayStartingDaysOfTheWeek.map(toIsoDayOfWeek).map((item) => ({id: item}));

/*
 * Component.
 */

export const DaysParameter = ({onDaysChange, days, ...rest}: DaysParameterProps) => {
  const localize = useLocalize();

  const onSetDays = useCallback(
    (isoDays: OrNull<ReadonlyArray<SelectedDropdownItem<{id: number}>>>) => {
      if (!isoDays) {
        return;
      }

      onDaysChange({
        days: isoDays
          .map((item) => {
            if (!isExpressionRef(item)) {
              return isoDayOfWeekToZeroNumbered(item);
            }

            // Expressions are not supperted yet here
            return undefined;
          })
          .filter(isDefined),
        timezone: currentTimezone(),
      });
    },
    [onDaysChange],
  );

  return (
    <MultipleChoiceDropdown
      {...rest}
      selectedItems={days?.map(zeroNumberedToIsoDayOfWeek)}
      onChange={onSetDays}
      items={items}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => renderForDay(item.id, localize)}
      serializeItemForSearch={(item) => renderForDay(item.id, localize)}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-days-param-placeholder">Select days</Trans>}
      title={rest.onSelectCreateExpression ? <Trans id="rule-editor-days-group">Days</Trans> : null}
    />
  );
};

/*
 * Helpers.
 */

function renderForDay(day: number, localize: Localizer): string {
  switch (fromIsoDayOfWeek(day)) {
    case 'mon':
      return localize(t('days-param-monday')`Monday`);

    case 'tue':
      return localize(t('days-param-tuesday')`Tuesday`);

    case 'wed':
      return localize(t('days-param-wednesday')`Wednesday`);

    case 'thu':
      return localize(t('days-param-thursday')`Thursday`);

    case 'fri':
      return localize(t('days-param-friday')`Friday`);

    case 'sat':
      return localize(t('days-param-saturday')`Saturday`);

    case 'sun':
      return localize(t('days-param-sunday')`Sunday`);

    default:
      return '';
  }
}
